import { api } from '@/lib/axios';
import type { UtilisateurDTO } from '@/services/auth';
import type { UserRole } from '@/types';

/* ------------------------------- Entreprise ------------------------------- */
export interface EntrepriseDTO {
  id: string;
  raison_sociale: string;
  forme_juridique: string | null;
  nif: string | null;
  nis: string | null;
  rc: string | null;
  ai: string | null;
  casnos_num: string | null;
  cnas_num: string | null;
  adresse: string | null;
  wilaya: string | null;
  telephone: string | null;
  email: string | null;
  logo_url: string | null;
  devise: string;
  exercice_fiscal_debut: string | null;
  prix_carburant_defaut: number;  // prix gasoil courant (DZD/L), pré-rempli sur les relevés
  jour_repos_hebdo: number;       // 0 = lundi … 6 = dimanche (4 = vendredi par défaut)
}

/** Jours de la semaine dans la convention du serveur (Python : lundi = 0). */
export const JOURS_SEMAINE = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
/** Convertit le jour de repos serveur (lundi=0) vers l'index JavaScript (dimanche=0). */
export const jourReposJs = (j: number | undefined) => ((j ?? 4) + 1) % 7;

export async function getEntreprise(): Promise<EntrepriseDTO> {
  const { data } = await api.get<EntrepriseDTO>('/entreprise');
  return data;
}

export async function updateEntreprise(payload: Partial<EntrepriseDTO>): Promise<EntrepriseDTO> {
  const { data } = await api.put<EntrepriseDTO>('/entreprise', payload);
  return data;
}

/* ---------------------------- Paramètres fiscaux -------------------------- */
export interface ParametresFiscauxDTO {
  id: string;
  libelle: string;
  tva_standard: number;
  tva_reduite: number;
  tap: number;
  cnas_salarie: number;
  cnas_patronal: number;
  iep_taux_par_an: number;
  iep_plafond_pct: number;
  irg_plafond_exoneration: number;
  seuil_paiement_especes_b2b: number;
  bareme_irg: Array<{ min: number; max: number | null; taux: number }>;
}

export async function getParametresFiscaux(): Promise<ParametresFiscauxDTO> {
  const { data } = await api.get<ParametresFiscauxDTO>('/parametres-fiscaux');
  return data;
}

export async function updateParametresFiscaux(
  payload: Partial<ParametresFiscauxDTO>,
): Promise<ParametresFiscauxDTO> {
  const { data } = await api.put<ParametresFiscauxDTO>('/parametres-fiscaux', payload);
  return data;
}

/* ----------------------------- Centres de coûts --------------------------- */
export type CentreCoutType =
  | 'SIEGE'
  | 'DIRECTION'
  | 'COMMERCIAL'
  | 'ADMINISTRATIF'
  | 'PARC_MATERIEL'
  | 'AUTRE';

export interface CentreCoutDTO {
  id: string;
  code: string;
  libelle: string;
  type: CentreCoutType;
  budget_annuel: number | null;
  is_active: boolean;
}

export interface CentreCoutInput {
  code: string;
  libelle: string;
  type: CentreCoutType;
  budget_annuel: number | null;
  is_active: boolean;
}

export async function listCentresCout(): Promise<CentreCoutDTO[]> {
  const { data } = await api.get<CentreCoutDTO[]>('/centres-cout');
  return data;
}

export async function createCentreCout(payload: CentreCoutInput): Promise<CentreCoutDTO> {
  const { data } = await api.post<CentreCoutDTO>('/centres-cout', payload);
  return data;
}

export async function updateCentreCout(
  id: string,
  payload: Partial<CentreCoutInput>,
): Promise<CentreCoutDTO> {
  const { data } = await api.put<CentreCoutDTO>(`/centres-cout/${id}`, payload);
  return data;
}

export async function deleteCentreCout(id: string): Promise<void> {
  await api.delete(`/centres-cout/${id}`);
}

/* ------------------------------ Utilisateurs ------------------------------ */
export interface UtilisateurInput {
  nom: string;
  prenom: string;
  email: string;
  role: UserRole;
  password?: string;
  is_active: boolean;
  permissions?: Record<string, string[]> | null;
}

export async function listUtilisateurs(): Promise<UtilisateurDTO[]> {
  const { data } = await api.get<UtilisateurDTO[]>('/utilisateurs');
  return data;
}

export async function createUtilisateur(payload: UtilisateurInput): Promise<UtilisateurDTO> {
  const { data } = await api.post<UtilisateurDTO>('/utilisateurs', payload);
  return data;
}

export async function updateUtilisateur(
  id: string,
  payload: Partial<UtilisateurInput>,
): Promise<UtilisateurDTO> {
  const { data } = await api.put<UtilisateurDTO>(`/utilisateurs/${id}`, payload);
  return data;
}

export async function deleteUtilisateur(id: string): Promise<void> {
  await api.delete(`/utilisateurs/${id}`);
}

/** Met à jour son propre compte (nom, prénom, email, mot de passe). */
export async function updateMonCompte(payload: { nom?: string; prenom?: string; email?: string; password?: string }): Promise<UtilisateurDTO> {
  const { data } = await api.put<UtilisateurDTO>('/utilisateurs/moi', payload);
  return data;
}
